import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'floatingCtaDismissed';

export const FloatingContactCTA = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === '1');
    const onScroll = () => setShow(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hide on the contact page itself
  const onContact = location.pathname === '/contact';
  const visible = show && !dismissed && !onContact;

  const goContact = () => {
    if (location.pathname === '/') {
      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/contact');
    }
  };

  const dismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0, scale: 0.9 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 80, opacity: 0, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 220, damping: 24 }}
          className="fixed bottom-5 right-4 sm:right-6 z-50"
        >
          <button
            onClick={goContact}
            className="group flex items-center gap-2.5 pl-4 pr-5 py-3 rounded-full gradient-premium text-primary-foreground shadow-hero hover:shadow-glow hover:-translate-y-0.5 transition-all duration-300"
            aria-label="Get in touch"
          >
            <MessageCircle size={18} className="group-hover:rotate-12 transition-transform duration-300" />
            <span className="text-sm font-semibold pr-1">Get in Touch</span>
            <span
              role="button"
              tabIndex={0}
              onClick={dismiss}
              onKeyDown={(e) => { if (e.key === 'Enter') dismiss(e as unknown as React.MouseEvent); }}
              aria-label="Dismiss"
              className="ml-1 -mr-1 w-6 h-6 rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 flex items-center justify-center transition-colors"
            >
              <X size={12} />
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
